import { parse, stringify, extract } from 'query-string';
import Cookies from '../../../core/js/services/cookies';
import {
    COOKIE_NAME_SITE_ID,
    COOKIE_NAME_INVENTORY_POOL
} from '../../../core/js/constants';

import {
    requestFacetedItems
} from '../actions/facetActions';
import { CATEGORY_REFINEMENT_NAMES } from '@core/js/constants';

/**
 * Construct a cache key from a list of slug strings
 * @param  {array} slugs A list of strings
 * @returns {string} The cache key
 */
export function cacheKey(slugs = []) {
    let key = '';

    slugs.forEach(function fn(slug) {
        key = key + slug;
    });
    return key;
}

export function getCleanSearchQueryString() {
    const cleanSearchQueryKeys = ['q'];
    const cleanQueryObj = _.pick(parse(extract(window.location.search)), cleanSearchQueryKeys);

    return stringify(cleanQueryObj);
}

export function getDefaultSortQueryString() {
    const toRemoveQueryKeys = ['sort', 'order'];
    const newQueryObj = _.omit(parse(extract(window.location.search)), toRemoveQueryKeys);

    return stringify(newQueryObj);
}

/**
 * Get Current Category
 * Gets the current category by splitting apart the URL and grabbing the first '/'-delimited string
 * after 'category/'
 * @returns {string} - current category slug
 */
export function getCurrentCategory() {
    const urlWithoutQueryString = window.location.href.split('?')[0];

    // TODO the 'category' part of this url needs to be a constant
    const categorySegment = urlWithoutQueryString.split('category/')[1];

    return categorySegment ? categorySegment.split('/')[0] : null;
}

/**
 * Gets route info about the page returned by the server
 * @returns {object} category params
 */
export function getRouteInfo() {
    const qs = extract(window.location.href);
    const queryParams = parse(qs);
    const searchTerm = queryParams.q || '';
    const currentPage = parseInt(queryParams.page, 10) || 1;
    const productsPerPage = 40;

    const categoryParams = {
        siteId: Cookies.getCookieByName(COOKIE_NAME_SITE_ID),
        poolId: Cookies.getCookieByName(COOKIE_NAME_INVENTORY_POOL),
        categoryId: getCurrentCategory(),
        searchTerm: searchTerm,
        isSearch: Boolean(searchTerm),
        pageSize: productsPerPage,
        skip: (currentPage - 1) * productsPerPage,
        currentPage: currentPage,
        queryString: qs
    };

    return categoryParams;
}

/**
 * Get Query String Without Facet Type
 * Gets the current query string minus the selected facets that are being cleared from filters
 * @param {string} qs - query string
 * @param {string} facetType - facet type
 * @returns {string} - Query string without facet type
 */
export function getQSWithoutFacetType(qs, facetType) {
    const parsedQS = parse(qs);
    const facetTypeValue = facetType.toLowerCase();
    const facetTypeToRemove = facetTypeValue;

    return _.omit(parsedQS, [facetTypeToRemove]);
}

export function onCategoryPopstate(event) {
    if (window.dontPopState) {
        return false;
    }
    this.attachLoader(this.store);
    if (event.state) {
        const categoryId = event.state.category.categorySlug;
        const facetValue = event.state.category.pageOptions.currentPage.queryString;

        this.dispatch(requestFacetedItems(categoryId, '', facetValue, true, facetValue, false, event, this));
    } else {
        const currentQueryString = extract(window.location.href);

        this.dispatch(requestFacetedItems('', '', '', true, currentQueryString, false, event, this));
    }
    return true;
}

function getOriginalFacetsHeight(component, $expandedFacetsPanel, lastFilerItemValue) {
    const expandedFacetsItem = $expandedFacetsPanel.find('.c-facet__ul')
        .children(`li[data-facet-value="${lastFilerItemValue}"]`);

    let offsetHeight = 0;

    if (component.getDeviceCharacteristics().is_smallish) {
        offsetHeight = $expandedFacetsPanel.find('.c-facets-modal__header').outerHeight(true) + 10;
    }

    return expandedFacetsItem.outerHeight(true) * expandedFacetsItem.index() + offsetHeight;
}

function scrollPositionForFacetsGroup($expandedFacetsPanel, scrollTopPosition) {
    if ($expandedFacetsPanel.is(':visible') && window.scrollTop !== scrollTopPosition) {
        $expandedFacetsPanel.animate({
            scrollTop: scrollTopPosition
        });
    }
}

function getFacetGroupForSelectedFacets(component, navigationName) {
    if (component.getDeviceCharacteristics().is_smallish) {
        return $(`.js-facet-modal-panel[data-facet-type="${navigationName}"]`);
    }
    return $(`.js-accordion-tabset[data-accordion-tab-name="${navigationName}"]`);
}

export function getLastFacetItemByFilterType(component) {
    const availableNavigation = _.get(component.getState(), 'category.products.availableNavigation');
    const lastFacetItemByFilterType = {};

    for (const navItem of availableNavigation) {
        const navigationName = navItem.name;
        const $facetGroup = getFacetGroupForSelectedFacets(component, navigationName);

        if (!_.has(navItem, 'userSelectedShowMore') &&
            _.has(navItem, 'moreRefinements') &&
            $facetGroup.is(':visible')) {
            lastFacetItemByFilterType[navigationName] = $facetGroup.find('li:last-child').attr('data-facet-value');
        }
    }
    return lastFacetItemByFilterType;
}

export function updateScrollPositionForFacets(component) {
    const state = component.getState();
    const navigationName = state.category.selectedFacet;
    const $expandedFacetsGroup = getFacetGroupForSelectedFacets(component, navigationName);
    let $expandedFacetsPanel = $expandedFacetsGroup.find('.js-accordion-panel');

    if (component.getDeviceCharacteristics().is_smallish) {
        $expandedFacetsPanel = $expandedFacetsGroup.find('.js-modal-inner');
    }

    const lastFilerItemValue = component.lastFacetItemByFilterType[navigationName];

    if (state.category.isSeeMoreSelected) {
        scrollPositionForFacetsGroup($expandedFacetsPanel, getOriginalFacetsHeight(component, $expandedFacetsPanel,
                                     lastFilerItemValue));
    } else {
        scrollPositionForFacetsGroup($expandedFacetsPanel, 0);
    }
}

/**
 * Get Custom Price Range Query String
 * Gets the custom price range query string based on user inputed or default values
 * @param {object} categoryState - state of category
 * @returns {string} - new query string
 */
export function getCustomPriceRangeQueryString(categoryState) {
    const facets = _.get(categoryState, 'facetOptions.nav', []);
    const priceFacet = _.find(facets, { 'name': CATEGORY_REFINEMENT_NAMES.PRICE });
    const { priceMin, priceMax } = getCustomPriceRangeValues(categoryState);

    const qs = _.get(priceFacet, 'customPriceRange.queryString');
    const parsedQueryString = parse(qs);
    parsedQueryString.price = `${priceMin}-${priceMax}`;
    return stringify(parsedQueryString);
}

/**
 * Get Custom Price Range Values
 * Gets the custom price range values based on user inputed or default values
 * @param {object} categoryState - state of category
 * @returns {object} - contains price min and max
 */
export function getCustomPriceRangeValues(categoryState) {
    const facets = _.get(categoryState, 'facetOptions.nav', []);
    const priceFacet = _.find(facets, { 'name': CATEGORY_REFINEMENT_NAMES.PRICE });
    const defaultMin = _.toInteger(_.get(categoryState, 'minPriceRange', 0));
    const defaultMax = _.toInteger(_.get(categoryState, 'maxPriceRange', 999999));
    const userMin = _.get(priceFacet, 'customPriceRange.min');
    const userMax = _.get(priceFacet, 'customPriceRange.max');
    let priceMin = userMin ? _.toInteger(userMin) : defaultMin;
    let priceMax = userMax ? _.toInteger(userMax) : defaultMax;

    if (priceMin > priceMax) {
        const temp = priceMin;
        priceMin = priceMax;
        priceMax = temp;
    }

    return { priceMin: priceMin, priceMax: priceMax };
}

/**
 * Logic to determine if disabled secondary navigation should be shown
 * (needed for client-side render of secondary nav & facets on device resize)
 * @param  {String} categoryContext        The category context from the redux store
 * @param  {Boolean} navTypeHidesNavigation Whether the current nav type would normally hide secondary navigation
 * @param  {Boolean} showSecondaryNav       Whether to normally show seconday navigation
 * @param  {Boolean} showFacets             Whether to show facets
 * @param  {Boolean} deviceIsSmallish       Is the user's screen size small or extra small
 * @return {Boolean}                        true if disabled secondary nav should be shown, false otherwise
 */
export function shouldShowDisabledNavigation(
    categoryContext,
    navTypeHidesNavigation,
    showSecondaryNav,
    showFacets,
    deviceIsSmallish) {
    const isSearch = (categoryContext === 'search');
    const isBrand = (categoryContext === 'brands');

    return (isSearch || isBrand || navTypeHidesNavigation || (!showSecondaryNav && showFacets)) && deviceIsSmallish;
}
